import { MapViewerService } from '@3ddv/ngx-dvm-internal';
import { AfterViewInit, Component, computed, effect, EffectRef, ElementRef, inject, Signal, ViewChild } from '@angular/core';
import { ButtonComponent } from '@components/shared/button/button.component';
import { APP_CONFIG } from '@configuration/configuration';
import { ButtonColors } from '@models/enums/components/Button/ButtonColor.enum';
import { ButtonType } from '@models/enums/components/Button/ButtonType.enum';
import { AppViews } from '@models/enums/dvm/DvmViews.enum';
import { Configuration } from '@models/interfaces/configuration/index.interface';
import { MapButtons } from '@models/types/components/MapButtons.type';
import { DvmService } from '@services/dvm.service';
import anime from 'animejs';

@Component({
  selector: 'app-zoom-buttons',
  standalone: true,
  imports: [
    ButtonComponent
  ],
  templateUrl: './zoom-buttons.component.html',
  styleUrl: './zoom-buttons.component.css'
})
export class ZoomButtonsComponent implements AfterViewInit {

  // VIEW CHILDREN
  @ViewChild('zoomInButton', { read: ElementRef }) 
  public zoomInButton!:         ElementRef<HTMLButtonElement>;

  @ViewChild('zoomOutButton', { read: ElementRef })
  public zoomOutButton!:        ElementRef<HTMLButtonElement>;

  // SERVICES
  private readonly mapViewer:   MapViewerService      = inject(DvmService).viewer;
  private readonly view:        Signal<AppViews|null> = inject(DvmService).view;
  private readonly configuration: Signal<Configuration>  = inject(APP_CONFIG);
  private  readonly mapButtons: Signal<MapButtons> = computed((): MapButtons=> this.configuration().Ui.MapButtons);
  

  // STATE 
  protected       showButtons:  boolean               = false;
  public readonly showZoomButtonElements = computed(() => this.mapButtons().zoom);
  
  // BUTTON STYLE
  public readonly type:         ButtonType            = ButtonType.Pill;
  public readonly color:        ButtonColors          = ButtonColors.Blur;


  // EFFECTS
  private readonly viewChange:  EffectRef             = effect((): void => {
    
    const view: AppViews | null = this.view();

    if(view === null) return;

    // Si es pano, ocultamos los botones
    if(view === AppViews.Pano) return this.showElements(false);
    
    // Si no es pano y no están mostrandose, mostramos los botones
    if(!this.showButtons) return this.showElements(true);

  })

  // GETTERS
  public get maxZoom(): boolean {
    
    return this.mapViewer.isInitialized() ? 
      this.mapViewer.scaling_factor === this.mapViewer.max_scaling_factor :
      false;

  }

  public get minZoom(): boolean {

    return this.mapViewer.isInitialized() ?
      this.mapViewer.scaling_factor === this.mapViewer.min_scaling_factor :
      false;

  }

  // LIFECYCLE HOOKS
  ngAfterViewInit(): void {
    this.initComponent();
  }

  // METHODS

  /**
   * Inicializa el componente:
   * Se espera a que el mapa esté inicializado para enlazar los botones de zoom.
   * @returns {void}
   */
  private initComponent(): void {

    this.mapViewer.waitInitialize().subscribe((): void => {

      this.mapViewer.bindInterfaceAction(this.zoomInButton.nativeElement, 'zoom-in');
      this.mapViewer.bindInterfaceAction(this.zoomOutButton.nativeElement, 'zoom-out');

      // Si el mapa está inicializado y no es pano, mostramos los botones
      setTimeout((): void => {
        if(this.view() !== AppViews.Pano) this.showElements(true);
      }, 500);

    })

  }

  /**
   * Método que anima los botones de zoom para aparecer o desaparecer.
   * @param show 
   */
  private showElements(show: boolean): void {
    
    const elements: HTMLElement[] = [
      this.zoomInButton.nativeElement.children[0] as HTMLElement,
      this.zoomOutButton.nativeElement.children[0] as HTMLElement
    ];

    anime({
      begin:    (): boolean =>  show ? this.showButtons = true : false,
      complete: (): boolean => !show ? this.showButtons = false : false,
      targets:  elements,
      opacity:  show ? [0,1] : [1,0],
      easing:   'easeInOutQuad',
      duration: 1000,
      delay:    anime.stagger(200)
    })

  }

}
